import { z } from "zod";

/**
 * One public key Ravnar signs its Revolut requests with, as RFC 7517 writes it. Strict: a JWK that also carries `d`,
 * `p`, `q`, `dp`, `dq` or `qi` is a private key, which must never be published, so it does not parse.
 */
const revolutJwkSchema = z.strictObject({
	kty: z.literal("RSA"),
	use: z.literal("sig"),
	kid: z.string().min(1),
	e: z.string().min(1),
	n: z.string().min(1),
	/** The certificate Revolut issued for the key, first in the chain, base64 DER. */
	x5c: z.array(z.string().min(1)).min(1),
});

/** The key set `GET /api/revolut/jwks` serves, which Revolut fetches to check Ravnar's signatures. */
export const revolutJwksSchema = z.strictObject({ keys: z.array(revolutJwkSchema).min(1) });
export type RevolutJwks = z.infer<typeof revolutJwksSchema>;

/** The owner or admin who connected Revolut: their member id, and the name they had then. */
export const revolutMemberSchema = z.strictObject({ id: z.string().min(1), name: z.string() });
export type RevolutMember = z.infer<typeof revolutMemberSchema>;

/**
 * Whether an organization is connected to Revolut, as `GET /api/revolut/connection` answers it:
 *
 * - `unavailable` — this deployment has no Revolut app registered, so nobody can connect;
 * - `not-connected` — nobody has connected, or somebody disconnected;
 * - `connected` — an owner or admin approved Ravnar's access in Revolut, and Revolut still honours it;
 * - `reconnect` — Revolut stopped honouring it: somebody withdrew Ravnar's access in Revolut, or it expired. Connecting
 *   again replaces it.
 *
 * Strict, and without a token in any of its shapes: the tokens behind a connection never leave the API.
 */
export const revolutConnectionSchema = z.discriminatedUnion("status", [
	z.strictObject({ status: z.literal("unavailable") }),
	z.strictObject({ status: z.literal("not-connected") }),
	z.strictObject({
		status: z.literal("connected"),
		connectedAt: z.iso.datetime(),
		connectedBy: revolutMemberSchema,
	}),
	z.strictObject({
		status: z.literal("reconnect"),
		connectedAt: z.iso.datetime(),
		connectedBy: revolutMemberSchema,
	}),
]);
export type RevolutConnection = z.infer<typeof revolutConnectionSchema>;
export type RevolutConnectionStatus = RevolutConnection["status"];

/**
 * What `POST /api/revolut/connection` answers: where to send the browser for an owner or admin to approve Ravnar's
 * access — Revolut's own page, or on the local API a path of the app that stands in for it.
 */
export const revolutAuthorizationSchema = z.strictObject({ authorizationUrl: z.string().min(1) });
export type RevolutAuthorization = z.infer<typeof revolutAuthorizationSchema>;

/**
 * What the browser brings back from Revolut, for `POST /api/revolut/connection/authorization`: the code Revolut gave
 * for the approval, and the state Ravnar sent with the request, which says the approval is the one it asked for.
 */
export const completeRevolutConnectionRequestSchema = z.strictObject({
	code: z.string().trim().min(1).max(512),
	state: z.string().trim().min(1).max(128),
});
export type CompleteRevolutConnectionRequest = z.infer<typeof completeRevolutConnectionRequestSchema>;

/**
 * The start of the state an approval of payments carries. Revolut sends the browser back to the one redirect URL
 * registered for the app whichever approval it was, and this tells the app's callback page that what came back allows
 * payments rather than connects.
 */
export const REVOLUT_PAYMENTS_STATE_PREFIX = "payments.";

/**
 * Whether Finance may send bills to Revolut as draft payments, as `GET /api/revolut/payments` answers it. A draft waits
 * in Revolut until the business's owner approves it there: Ravnar never sends money itself.
 *
 * - `unavailable` — this deployment has no Revolut app registered;
 * - `not-connected` — the organization is not connected to Revolut, which payments are allowed beside;
 * - `not-allowed` — nobody has allowed them, or somebody stopped them;
 * - `allowed` — an owner or admin allowed them in Revolut, and Revolut still honours it;
 * - `reconnect` — Revolut stopped honouring it: somebody withdrew it in Revolut, or it expired. Allowing them again
 *   replaces it.
 *
 * Strict, and without a token in any of its shapes: the tokens behind them never leave the API.
 */
export const revolutPaymentsSchema = z.discriminatedUnion("status", [
	z.strictObject({ status: z.literal("unavailable") }),
	z.strictObject({ status: z.literal("not-connected") }),
	z.strictObject({ status: z.literal("not-allowed") }),
	z.strictObject({ status: z.literal("allowed"), allowedAt: z.iso.datetime(), allowedBy: revolutMemberSchema }),
	z.strictObject({ status: z.literal("reconnect"), allowedAt: z.iso.datetime(), allowedBy: revolutMemberSchema }),
]);
export type RevolutPayments = z.infer<typeof revolutPaymentsSchema>;

/** An ISO 4217 currency code, as Revolut writes it. */
const revolutCurrencySchema = z.string().regex(/^[A-Z]{3}$/, "Invalid currency");

/**
 * An amount of money as a bank states it: a decimal string, negative when it is owed, and never a float, so no cent is
 * lost on the way. Revolut sends up to five decimals.
 */
export const revolutAmountSchema = z.strictObject({
	amount: z.string().regex(/^-?\d{1,13}(?:\.\d{1,5})?$/, "Invalid amount"),
	currency: revolutCurrencySchema,
});
export type RevolutAmount = z.infer<typeof revolutAmountSchema>;

/** The ways Revolut names an account to pay into, as the Open Banking API spells them. */
export const REVOLUT_ACCOUNT_SCHEMES = [
	"UK.OBIE.IBAN",
	"UK.OBIE.SortCodeAccountNumber",
	"US.RoutingNumberAccountNumber",
	"US.BranchCodeAccountNumber",
	"UK.Revolut.InternalAccountId",
] as const;
export type RevolutAccountScheme = (typeof REVOLUT_ACCOUNT_SCHEMES)[number];

/**
 * One account a connection can read: each currency of a Revolut account is an account of its own, with an id of its
 * own, though several may share one IBAN.
 */
export const revolutAccountSchema = z.strictObject({
	/** Revolut's id for it, unique and fixed. */
	id: z.string().min(1).max(40),
	/** What it is called: its nickname in Revolut, or its currency and kind when it has none. */
	name: z.string().min(1).max(70),
	currency: revolutCurrencySchema,
	type: z.enum(["Business", "Personal"]),
	subtype: z.enum(["CurrentAccount", "Savings", "CreditCard"]),
	/** How to pay into it, when Revolut says: an IBAN, a sort code and account number, and whose name it is in. */
	identifications: z.array(
		z.strictObject({
			scheme: z.enum(REVOLUT_ACCOUNT_SCHEMES),
			identification: z.string().min(1).max(256),
			name: z.string().max(70).optional(),
		}),
	),
	/** What is available in it now, or undefined when Revolut gave no balance for it. */
	balance: revolutAmountSchema.optional(),
});
export type RevolutAccount = z.infer<typeof revolutAccountSchema>;

/**
 * What `GET /api/revolut/accounts` answers: the connected accounts with their balances, read from Revolut there and
 * then, or why there are none to read.
 */
export const revolutAccountsSchema = z.discriminatedUnion("status", [
	z.strictObject({ status: z.literal("unavailable") }),
	z.strictObject({ status: z.literal("not-connected") }),
	z.strictObject({ status: z.literal("reconnect") }),
	z.strictObject({ status: z.literal("connected"), accounts: z.array(revolutAccountSchema) }),
]);
export type RevolutAccounts = z.infer<typeof revolutAccountsSchema>;

/** How many of an account's transactions `GET /api/revolut/accounts/{accountId}/transactions` answers at most. */
export const REVOLUT_TRANSACTIONS_MAX = 100;

/**
 * One transaction on an account, as `GET /api/revolut/accounts/{accountId}/transactions` answers it: booked, or still
 * pending, and what the bank says about it.
 */
export const revolutTransactionSchema = z.strictObject({
	/** Revolut's id for it. */
	id: z.string().min(1).max(120),
	/** When it was booked, or, while it is pending, when it was made. */
	at: z.iso.datetime(),
	status: z.enum(["booked", "pending"]),
	/** Negative when money left the account. */
	amount: revolutAmountSchema,
	/** What it was, as the bank words it: a merchant, "To Northwind Systems", "Exchanged to EUR". */
	description: z.string().min(1).max(500),
	/** Who was paid, or who paid, when the bank names them. */
	counterparty: z.string().min(1).max(350).optional(),
});
export type RevolutTransaction = z.infer<typeof revolutTransactionSchema>;

/**
 * What `GET /api/revolut/accounts/{accountId}/transactions` answers: the account's latest transactions, newest first,
 * read from Revolut there and then — or why there are none to read.
 */
export const revolutTransactionsSchema = z.discriminatedUnion("status", [
	z.strictObject({ status: z.literal("unavailable") }),
	z.strictObject({ status: z.literal("not-connected") }),
	z.strictObject({ status: z.literal("reconnect") }),
	z.strictObject({
		status: z.literal("connected"),
		transactions: z.array(revolutTransactionSchema).max(REVOLUT_TRANSACTIONS_MAX),
	}),
]);
export type RevolutTransactions = z.infer<typeof revolutTransactionsSchema>;

/** The most Revolut's sandbox adds to an account in one top-up. */
export const REVOLUT_SANDBOX_TOP_UP_MAX = 10_000;

/** The states Revolut's sandbox can make a top-up in: `completed`, unless another is asked for. */
export const REVOLUT_SANDBOX_TOP_UP_STATES = ["pending", "completed", "reverted", "failed"] as const;

/**
 * What `POST /api/revolut/sandbox/top-ups` takes — a development tool, open, served on the dev stage alone: which of an
 * organization's Revolut accounts to top up in Revolut's sandbox, and by how much. The top-up is in the account's own
 * currency, which may be named to make sure of it; Revolut's sandbox gives it the reference "Test Top-up" and the state
 * `completed` unless others are asked for.
 */
export const revolutSandboxTopUpRequestSchema = z.strictObject({
	organizationId: z.string().trim().min(1).max(64),
	/** The account's id as Ravnar has it: `GET /api/revolut/accounts` lists it, and the Revolut page puts it in its URL. */
	accountId: z.string().trim().min(1).max(40),
	amount: z.number().positive().max(REVOLUT_SANDBOX_TOP_UP_MAX),
	currency: revolutCurrencySchema.optional(),
	reference: z.string().trim().min(1).max(100).optional(),
	state: z.enum(REVOLUT_SANDBOX_TOP_UP_STATES).optional(),
});
export type RevolutSandboxTopUpRequest = z.infer<typeof revolutSandboxTopUpRequestSchema>;

/** What `POST /api/revolut/sandbox/top-ups` answers: the top-up as Revolut's sandbox made it. */
export const revolutSandboxTopUpSchema = z.strictObject({
	/** The id Revolut's sandbox answered for the top-up. */
	id: z.string().min(1).max(64),
	state: z.enum(REVOLUT_SANDBOX_TOP_UP_STATES),
	createdAt: z.iso.datetime(),
	/** When it was completed, once it is. */
	completedAt: z.iso.datetime().optional(),
});
export type RevolutSandboxTopUp = z.infer<typeof revolutSandboxTopUpSchema>;

/**
 * The audit entry for an owner or admin starting to connect Revolut: "Started connecting Revolut". Recorded once the
 * approval is kept, before Revolut's page asks them; "Connected Revolut" follows once they approve it there.
 */
export function revolutConnectionStartedAudit(): { action: string; detail: string } {
	return { action: "Started connecting Revolut", detail: "" };
}

/** The audit entry for an organization connecting Revolut: "Connected Revolut". */
export function revolutConnectedAudit(): { action: string; detail: string } {
	return { action: "Connected Revolut", detail: "" };
}

/** The audit entry for an organization disconnecting Revolut: "Disconnected Revolut". */
export function revolutDisconnectedAudit(): { action: string; detail: string } {
	return { action: "Disconnected Revolut", detail: "" };
}

/**
 * The audit entry for an owner or admin starting to let Finance send bills to Revolut: "Started allowing Revolut
 * payments". Recorded once the approval is kept, before Revolut Business asks them; "Allowed Revolut payments" follows
 * once they allow it there.
 */
export function revolutPaymentsStartedAudit(): { action: string; detail: string } {
	return { action: "Started allowing Revolut payments", detail: "" };
}

/** The audit entry for an owner or admin letting Finance send bills to Revolut: "Allowed Revolut payments". */
export function revolutPaymentsAllowedAudit(): { action: string; detail: string } {
	return { action: "Allowed Revolut payments", detail: "" };
}

/** The audit entry for one taking that back: "Stopped Revolut payments". */
export function revolutPaymentsStoppedAudit(): { action: string; detail: string } {
	return { action: "Stopped Revolut payments", detail: "" };
}
